import { liveQuery } from "dexie";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { calculateFinalPayableRounding } from "../runtime/commerce/final-payable-rounding";
import { resolveLocalSaleFinalPayable } from "../runtime/commerce/local-sale-final-payable";
import type { CommerceCalculationRuntimeItem, CustomerCreditRuntimeItem } from "../runtime/gateway/local-projection-client";
import type {
  PaymentMethodRuntimeProjection,
  PaymentReceivingDestinationProjection,
} from "../runtime/gateway/payment-runtime-client";
import {
  commitLocalCashSale,
  freshLocalSaleQuote,
  syncCashSaleOutbox,
  syncPendingCashSales,
  type LocalCashSaleSnapshot,
} from "../runtime/local-store/cash-sale-state";
import { readLocalCart, saveLocalCart, type LocalCartDraft } from "../runtime/local-store/cart-state";
import { readCatalogPriceRuntime } from "../runtime/local-store/catalog-price-runtime";
import {
  readCommerceCalculationRuntime,
  readPublishedCommerceCalculationRuntime,
} from "../runtime/local-store/commerce-calculation-runtime";
import {
  currentCartRuntimeScopeKey,
  readCurrentCartRuntime,
  subscribeCurrentCartRuntime,
} from "../runtime/local-store/current-cart-runtime";
import {
  commitLocalCreditSaleIntent,
  readPendingLocalCreditExposure,
  syncCreditSaleOutbox,
  syncPendingCreditSales,
  type LocalCreditSaleSnapshot,
} from "../runtime/local-store/credit-sale-state";
import {
  readLocalEmployeeSession,
  type LocalEmployeeSessionState,
} from "../runtime/local-store/employee-session";
import { readLocalCustomerCredit } from "../runtime/local-store/indexed-runtime";
import {
  readLocalLoyaltyRedemption,
  type LocalLoyaltyRedemption,
} from "../runtime/local-store/loyalty-redemption-state";
import {
  commitLocalNonCashSale,
  syncNonCashSaleOutbox,
  syncPendingNonCashSales,
  type LocalNonCashSaleSnapshot,
} from "../runtime/local-store/noncash-sale-state";
import { readLocalShift, type LocalShiftState } from "../runtime/local-store/shift-state";
import {
  readTerminalCredential,
  type TerminalCredentialState,
} from "../runtime/local-store/terminal-state";
import { usePosPaymentRuntime } from "../runtime/payment/use-pos-payment-runtime";
import {
  clearPromotionClaimSession,
  readPromotionClaimSession,
  subscribePromotionClaimSession,
} from "../runtime/promotion/promotion-claim-session";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
  shift: LocalShiftState | null;
  cart: LocalCartDraft | null;
  loyaltyRedemption: LocalLoyaltyRedemption | null;
  customerCredit: CustomerCreditRuntimeItem | null;
  pendingLocalCreditExposure: number;
  catalogRevision: number;
  commerceCalculation: CommerceCalculationRuntimeItem | null;
};

type CheckoutState = "IDLE" | "COMMITTING" | "SYNCING" | "CONFIRMED" | "PENDING" | "ERROR";
type PaymentGroup = "" | "CASH" | "NONCASH" | "CREDIT";
type CommittedSale = LocalCashSaleSnapshot | LocalNonCashSaleSnapshot | LocalCreditSaleSnapshot;

const compatibilityCashMethod: PaymentMethodRuntimeProjection = {
  payment_method_code: "CASH",
  payment_method_name: "เงินสด",
  runtime_capability: "OFFLINE_CAPABLE",
  settlement_mode: "IMMEDIATE_CASH",
  provider_required: false,
  ready: true,
  reason_code: null,
  primary_destination: null,
  fallback_destinations: [],
};

function formatMoney(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDeferredPaymentAvailableCredit(value: number, currency = "THB") {
  if (currency.trim().toUpperCase() === "THB") {
    const amount = new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
    return `${amount} บาท`;
  }
  return formatMoney(value, currency);
}

function detailText(details: Record<string, unknown> | undefined, key: string) {
  const value = details?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function paymentRuntimeReason(method: PaymentMethodRuntimeProjection) {
  if (method.reason_code === "PAYMENT_RECEIVING_PRIMARY_NOT_CONFIGURED") {
    return "ยังไม่ได้ตั้งช่องทางรับเงินหลักใน Back Office";
  }
  if (method.reason_code === "PAYMENT_METHOD_NOT_ENABLED_FOR_BRANCH") {
    return "วิธีชำระเงินนี้ไม่ได้เปิดใช้สำหรับสาขานี้";
  }
  return method.reason_code ? "การตั้งค่าวิธีชำระเงินนี้ยังไม่พร้อม" : null;
}

function canCommitNonCashLocally(method: PaymentMethodRuntimeProjection | null) {
  return Boolean(
    method
      && !method.provider_required
      && ["OFFLINE_CAPABLE", "OFFLINE_PENDING_ALLOWED"].includes(method.runtime_capability),
  );
}

function creditReason(
  credit: CustomerCreditRuntimeItem | null,
  total: number,
  currency: string,
  effectiveAvailableCredit: number | null,
) {
  if (!credit) return "ลูกค้าคนนี้ยังไม่ได้เปิดสิทธิ์ค้างชำระใน Back Office";
  if (credit.status === "SUSPENDED") return "สิทธิ์ค้างชำระถูกระงับชั่วคราว";
  if (credit.status === "CLOSED") return "บัญชีเครดิตถูกปิดแล้ว";
  if (!credit.branch_eligible) return "สิทธิ์ค้างชำระใช้ไม่ได้ที่สาขานี้";
  if (credit.credit_limit == null || credit.payment_term_days == null) return "ยังตั้งวงเงินหรือกำหนดชำระไม่ครบใน Back Office";
  if (credit.currency_code !== currency) return "สกุลเงินของบัญชีเครดิตไม่ตรงกับบิลนี้";
  if (!credit.eligible) {
    if (credit.eligibility_reason === "CREDIT_LIMIT_EXCEEDED") return "วงเงินคงเหลือไม่พอ";
    if (credit.eligibility_reason === "CREDIT_TERMS_NOT_CONFIGURED") return "ยังตั้งเงื่อนไขเครดิตไม่ครบใน Back Office";
    return "สิทธิ์ค้างชำระยังใช้ไม่ได้ในขณะนี้";
  }
  if (effectiveAvailableCredit == null) return "ยังคำนวณวงเงินคงเหลือไม่ได้";
  if (total > effectiveAvailableCredit) return "วงเงินคงเหลือไม่พอสำหรับบิลนี้";
  return null;
}

function friendlyError(value: string) {
  if (value.includes("CREDIT_SALE_REQUIRES_ONLINE")) return "ค้างชำระต้องเชื่อมต่ออินเทอร์เน็ตเพื่อยืนยันวงเงินล่าสุด";
  if (value.includes("CREDIT_SALE_CUSTOMER_REQUIRED")) return "ต้องเลือกลูกค้าก่อนบันทึกค้างชำระ";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_ACCOUNT_NOT_FOUND")) return "ลูกค้าคนนี้ยังไม่ได้เปิดสิทธิ์ค้างชำระใน Back Office";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_ACCOUNT_SUSPENDED")) return "สิทธิ์ค้างชำระถูกระงับชั่วคราว";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_ACCOUNT_CLOSED")) return "บัญชีเครดิตถูกปิดแล้ว";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_BRANCH_NOT_ELIGIBLE")) return "สิทธิ์ค้างชำระใช้ไม่ได้ที่สาขานี้";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_TERMS_NOT_CONFIGURED")) return "กรุณาตั้งวงเงินและกำหนดชำระใน Back Office ให้ครบก่อน";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_LIMIT_EXCEEDED")) return "วงเงินคงเหลือไม่พอสำหรับบิลนี้";
  if (value.includes("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_CURRENCY_MISMATCH")) return "สกุลเงินของบัญชีเครดิตไม่ตรงกับบิลนี้";
  if (value.includes("NONCASH_PAYMENT_FINAL_CONFIRM_REQUIRES_ONLINE")) return "การยืนยันสแกน/โอนต้องเชื่อมต่ออินเทอร์เน็ต";
  if (value.includes("NONCASH_PAYMENT_LOCAL_COMMIT_NOT_ALLOWED")) return "วิธีชำระเงินนี้ต้องยืนยันกับระบบภายนอกก่อน จึงยังปิดบิลไม่ได้";
  if (value.includes("PAYMENT_RECEIVING_DESTINATION_NOT_ELIGIBLE")) return "ช่องทางรับเงินนี้ไม่ได้อยู่ในช่องทางหลัก/สำรองที่ร้านอนุญาต กรุณาอัปเดตข้อมูลช่องทางรับเงิน";
  if (value.includes("PAYMENT_RECEIVING_DESTINATION_REQUIRED")) return "ยังไม่มีช่องทางรับเงินที่พร้อมใช้";
  if (value.includes("PAYMENT_METHOD_NOT_ENABLED_FOR_BRANCH")) return "วิธีชำระเงินนี้ไม่ได้เปิดใช้สำหรับสาขานี้";
  if (value.includes("PAYMENT_RECEIVING_PRIMARY_NOT_CONFIGURED")) return "ยังไม่ได้ตั้งช่องทางรับเงินหลักใน Back Office";
  if (value.includes("ACTIVE_POS_SHIFT_REQUIRED")) return "ต้องมีกะที่เปิดอยู่ก่อนชำระเงิน";
  if (value.includes("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์ยืนยันการขาย";
  if (value.includes("PUBLISHED_MODIFIER_CONSUMPTION_EFFECT_RUNTIME_REQUIRED")) {
    return "ตัวเลือกนี้ยังไม่พร้อมตัดสต็อก กรุณาเลือกตัวเลือกอื่น";
  }
  if (value.includes("MODIFIER_QUANTITY_SYNC_NOT_SUPPORTED")) return "ตัวเลือกนี้ยังไม่รองรับจำนวนมากกว่า 1";
  if (value.includes("PROMOTION_FACT_PERSISTENCE_NOT_READY")) return "บิลที่มีโปรโมชั่นอัตโนมัติยังไม่พร้อมรับชำระ กรุณาตรวจโปรโมชั่นอีกครั้ง";
  if (value.includes("PROMOTION_CLAIM_REQUIRES_ONLINE")) return "คูปองต้องเชื่อมต่ออินเทอร์เน็ตเพื่อยืนยันสิทธิ์";
  if (value.includes("PROMOTION_CLAIM_SERVER_QUOTE_REQUIRED") || value.includes("PROMOTION_CLAIM_QUOTE_VALIDATION_REQUIRED")) return "กรุณาตรวจรหัสคูปองอีกครั้งก่อนรับเงิน";
  if (value.includes("PROMOTION_CLAIM_REQUOTE_CHANGED")) return "สิทธิ์คูปองเปลี่ยนระหว่างชำระ ระบบยังไม่รับเงิน กรุณาตรวจคูปองใหม่";
  if (value.includes("PROMOTION_CLAIM_CODE_NOT_FOUND")) return "ไม่พบรหัสคูปอง กรุณาล้างหรือกรอกรหัสใหม่";
  if (value.includes("PROMOTION_CLAIM_EXPIRED")) return "คูปองหมดอายุแล้ว กรุณาล้างรหัสออกจากบิล";
  if (value.includes("PROMOTION_CLAIM_CONSUMED")) return "คูปองนี้ถูกใช้แล้ว กรุณาล้างรหัสออกจากบิล";
  if (value.includes("LOYALTY_REWARD_RESERVATION_EXPIRED")) return "สิทธิ์รางวัลหมดอายุแล้ว กรุณาเลือกรางวัลใหม่";
  if (value.includes("LOYALTY_REWARD_")) return "สิทธิ์รางวัลสมาชิกเปลี่ยนแล้ว กรุณาตรวจรางวัลก่อนรับเงิน";
  if (value.includes("TENDERED_AMOUNT")) return "จำนวนเงินสดที่รับมายังไม่ถูกต้อง";
  if (value.includes("LOCAL_TOTAL_MISMATCH") || value.includes("LOCAL_PRICE_AMOUNT_MISMATCH") || value.includes("PRICE_VERSION_MISMATCH")) {
    return "ราคามีการเปลี่ยนแปลง กรุณาอัปเดตข้อมูลสินค้าแล้วเริ่มบิลใหม่";
  }
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณา Login ใหม่";
  if (value.includes("GATEWAY_UNREACHABLE") || value.includes("GATEWAY_TIMEOUT")) return "ยังส่งข้อมูลรายการไม่สำเร็จ ระบบจะดำเนินการต่ออัตโนมัติเมื่อเชื่อมต่อพร้อม";
  return value || "ทำรายการไม่สำเร็จ";
}

function DestinationCard({
  destination,
  role,
}: {
  destination: PaymentReceivingDestinationProjection;
  role: "PRIMARY" | "FALLBACK";
}) {
  const accountName = detailText(destination.public_details, "account_name");
  const accountIdentifier = detailText(destination.public_details, "account_identifier");
  const instructions = detailText(destination.public_details, "instructions");
  return (
    <div className="cash-sale-modal__destination">
      <div className="cash-sale-modal__destination-head">
        <div>
          <p>{role === "PRIMARY" ? "ช่องทางรับเงินหลัก" : "ช่องทางรับเงินสำรอง"}</p>
          <strong>{destination.display_name}</strong>
        </div>
        {destination.provider_code ? <span>{destination.provider_code}</span> : null}
      </div>
      {accountName ? <p>ชื่อผู้รับ: {accountName}</p> : null}
      {accountIdentifier ? <p className="cash-sale-modal__destination-id">{accountIdentifier}</p> : null}
      {instructions ? <p>{instructions}</p> : null}
    </div>
  );
}

function isNonCashSale(sale: CommittedSale | null): sale is LocalNonCashSaleSnapshot {
  return sale?.schema === "LOCAL_NONCASH_SALE_V1";
}

function isCreditSale(sale: CommittedSale | null): sale is LocalCreditSaleSnapshot {
  return sale?.schema === "LOCAL_CREDIT_SALE_V1";
}

function isCashSale(sale: CommittedSale | null): sale is LocalCashSaleSnapshot {
  return sale?.schema === "LOCAL_CASH_SALE_V1";
}

export function CashSaleRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({
    terminal: null,
    session: null,
    shift: null,
    cart: null,
    loyaltyRedemption: null,
    customerCredit: null,
    pendingLocalCreditExposure: 0,
    catalogRevision: 0,
    commerceCalculation: null,
  });
  const [online, setOnline] = useState(navigator.onLine);
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutCalculation, setCheckoutCalculation] = useState<CommerceCalculationRuntimeItem | null>(null);
  const [selectedPaymentGroup, setSelectedPaymentGroup] = useState<PaymentGroup>("");
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState("");
  const [selectedDestinationId, setSelectedDestinationId] = useState("");
  const [showFallbacks, setShowFallbacks] = useState(false);
  const [tendered, setTendered] = useState("");
  const [checkoutState, setCheckoutState] = useState<CheckoutState>("IDLE");
  const [checkoutMessage, setCheckoutMessage] = useState<string | null>(null);
  const [committedSale, setCommittedSale] = useState<CommittedSale | null>(null);
  const [pendingNonCashIntent, setPendingNonCashIntent] = useState<LocalNonCashSaleSnapshot | null>(null);
  const [pendingCreditIntent, setPendingCreditIntent] = useState<LocalCreditSaleSnapshot | null>(null);
  const claimSession = useSyncExternalStore(
    subscribePromotionClaimSession,
    readPromotionClaimSession,
    readPromotionClaimSession,
  );
  const currentCartRuntime = useSyncExternalStore(
    subscribeCurrentCartRuntime,
    readCurrentCartRuntime,
    readCurrentCartRuntime,
  );
  const paymentRuntime = usePosPaymentRuntime(runtime.terminal, online);

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const terminal = await readTerminalCredential();
      const identity = terminal?.identity ?? null;
      const [session, shift, cart, catalog, commerceCalculation] = await Promise.all([
        readLocalEmployeeSession(),
        readLocalShift(),
        identity ? readLocalCart(identity) : Promise.resolve(null),
        identity ? readCatalogPriceRuntime(identity) : Promise.resolve(null),
        identity ? readCommerceCalculationRuntime(identity) : Promise.resolve(null),
      ]);
      const loyaltyRedemption = identity && cart
        ? await readLocalLoyaltyRedemption(identity, cart.requestKey)
        : null;
      const [customerCredit, pendingLocalCreditExposure] = identity && cart?.selectedMember
        ? await Promise.all([
            readLocalCustomerCredit(identity, cart.selectedMember.customerId),
            readPendingLocalCreditExposure(identity, cart.selectedMember.customerId),
          ])
        : [null, 0];
      return {
        terminal,
        session,
        shift,
        cart,
        loyaltyRedemption,
        customerCredit,
        pendingLocalCreditExposure,
        catalogRevision: catalog?.catalogRevision ?? 0,
        commerceCalculation,
      } satisfies RuntimeSnapshot;
    }).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, session: null, shift: null, cart: null, loyaltyRedemption: null, customerCredit: null, pendingLocalCreditExposure: 0, catalogRevision: 0, commerceCalculation: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateNetwork = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
    };
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-cart-panel"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const terminal = runtime.terminal;
  const identity = terminal?.identity ?? null;
  const currentScopeKey = identity ? currentCartRuntimeScopeKey(identity) : null;
  const currentCartRuntimeAuthoritative = Boolean(currentScopeKey && currentCartRuntime.scopeKey === currentScopeKey);
  const session = runtime.session;
  const actor = session?.actor ?? null;
  const shift = runtime.shift?.shift ?? null;
  const cart = currentCartRuntimeAuthoritative ? currentCartRuntime.cart : runtime.cart;
  const customerCredit = runtime.customerCredit;
  const commerceCalculation = showCheckout && checkoutCalculation ? checkoutCalculation : runtime.commerceCalculation;
  const cartLines = cart?.lines ?? [];
  const subtotal = useMemo(
    () => cartLines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0),
    [cartLines],
  );
  const quote = cart ? freshLocalSaleQuote(cart) : null;
  const preRoundingTotal = quote?.postPromotionTotal ?? subtotal;
  const currency = quote?.currencyCode ?? cartLines[0]?.currencyCode ?? "THB";
  const baseFinalPayable = quote && commerceCalculation
    ? calculateFinalPayableRounding(quote.postPromotionTotal, commerceCalculation)
    : null;
  let finalPayable = baseFinalPayable;
  if (baseFinalPayable && cart) {
    try {
      finalPayable = resolveLocalSaleFinalPayable(baseFinalPayable, runtime.loyaltyRedemption, cart, currency);
    } catch {
      finalPayable = null;
    }
  }
  const checkoutTotal = finalPayable?.totalAmount ?? preRoundingTotal;
  const displayTotal = committedSale?.canonical?.grand_total ?? committedSale?.localTotal ?? checkoutTotal;
  const displayCurrency = committedSale?.canonical?.currency_code ?? committedSale?.currencyCode ?? currency;
  const tenderedAmount = Number(tendered);
  const change = Number.isFinite(tenderedAmount) ? Math.max(0, tenderedAmount - checkoutTotal) : 0;

  const configuredPaymentMethods = paymentRuntime.runtime.payload?.payment_methods ?? [];
  const paymentMethods = paymentRuntime.runtime.initialized
    ? configuredPaymentMethods
    : [compatibilityCashMethod];
  const cashMethod = paymentMethods.find((method) => method.settlement_mode === "IMMEDIATE_CASH") ?? null;
  const nonCashMethods = paymentMethods.filter((method) => method.settlement_mode === "RECEIVING_DESTINATION");
  const readyNonCashMethods = nonCashMethods.filter(
    (method) => method.ready && canCommitNonCashLocally(method) && Boolean(method.primary_destination),
  );
  const selectedMethod = paymentMethods.find((method) => method.payment_method_code === selectedPaymentMethodCode) ?? null;
  const selectedDestination = selectedMethod?.primary_destination
    ? [selectedMethod.primary_destination, ...(selectedMethod.fallback_destinations ?? [])]
        .find((destination) => destination.destination_id === selectedDestinationId) ?? selectedMethod.primary_destination
    : null;
  const selectedDestinationRole: "PRIMARY" | "FALLBACK" = selectedDestination
    && selectedMethod?.primary_destination?.destination_id !== selectedDestination.destination_id
    ? "FALLBACK"
    : "PRIMARY";
  const isCashSelected = selectedPaymentGroup === "CASH" && selectedMethod?.settlement_mode === "IMMEDIATE_CASH";
  const isNonCashSelected = selectedPaymentGroup === "NONCASH";
  const isCreditSelected = selectedPaymentGroup === "CREDIT";
  const canonicalAvailableCredit = customerCredit?.available_credit ?? null;
  const effectiveAvailableCredit = canonicalAvailableCredit == null
    ? null
    : Math.max(0, canonicalAvailableCredit - runtime.pendingLocalCreditExposure);
  const localCreditReason = creditReason(customerCredit, checkoutTotal, currency, effectiveAvailableCredit);
  const creditAvailabilityCopy = customerCredit && canonicalAvailableCredit != null
    ? `วงเงินพร้อมใช้ ${formatDeferredPaymentAvailableCredit(canonicalAvailableCredit, customerCredit.currency_code)} · ชำระภายใน ${customerCredit.payment_term_days} วัน${runtime.pendingLocalCreditExposure > 0 && effectiveAvailableCredit != null
      ? ` · รอส่งข้อมูล ${formatDeferredPaymentAvailableCredit(runtime.pendingLocalCreditExposure, customerCredit.currency_code)} · ใช้กับบิลใหม่ได้ ${formatDeferredPaymentAvailableCredit(effectiveAvailableCredit, customerCredit.currency_code)}`
      : ""}${localCreditReason ? ` · ${localCreditReason}` : ""}`
    : localCreditReason;
  const localCreditEligible = Boolean(cart?.selectedMember && customerCredit && finalPayable && !localCreditReason);
  const hasUsablePayment = Boolean(cashMethod?.ready || readyNonCashMethods.length);
  const noPaymentRequired = Boolean(finalPayable && finalPayable.totalAmount === 0);
  const hasCheckoutCapability = noPaymentRequired || hasUsablePayment || localCreditEligible;

  const sameScope = Boolean(
    actor && identity
      && actor.organization_id === identity.organization_id
      && actor.branch_id === identity.branch_id
      && actor.node_id === identity.node_id
      && actor.runtime_session_status === "ACTIVE",
  );
  const canCommit = Boolean(actor?.permission_codes.includes("commerce.sale.commit"));
  const activeShift = shift?.shift_status === "OPEN";
  const hasPromotionClaim = Boolean(claimSession.credentialCode);
  const hasCustomerPromotionClaim = Boolean(claimSession.customerClaim);
  const hasBearerPromotionClaim = Boolean(hasPromotionClaim && !hasCustomerPromotionClaim);
  const customerPromotionClaimReady = Boolean(
    !hasCustomerPromotionClaim || (
      quote?.appliedPromotions.some(
        (promotion) => promotion.activationCode === "CLAIM_CODE"
          && promotion.offlineCapability === "OFFLINE_RISK_LIMITED",
      )
      && cart?.selectedMember?.customerId === claimSession.customerClaim?.customerId
    )
  );
  const promotionClaimReady = Boolean(
    customerPromotionClaimReady && (
      !hasBearerPromotionClaim || (
        online
        && quote?.quoteSource === "SERVER_COMMERCE_QUOTE_V1"
        && claimSession.evaluation
        && claimSession.evaluation.validation_state !== "INVALID"
      )
    )
  );
  const hasPromotionContext = Boolean(cart?.claimedPromotionVersionIds.length || quote?.appliedPromotions.length || (quote?.promotionDiscountTotal ?? 0) > 0 || hasPromotionClaim);
  const unsafeAppliedPromotion = Boolean(quote?.appliedPromotions.some((promotion) => {
    if (!promotion.offlineCapability || promotion.offlineCapability === "OFFLINE_SAFE") return false;
    return !(
      hasCustomerPromotionClaim
      && promotion.activationCode === "CLAIM_CODE"
      && promotion.offlineCapability === "OFFLINE_RISK_LIMITED"
    );
  }));
  const promotionRequiresOnline = Boolean(hasBearerPromotionClaim || quote?.requiresOnlineResolution || unsafeAppliedPromotion);
  const saleContextRequiresOnline = hasPromotionContext && promotionRequiresOnline;
  const ready = Boolean(
    cartLines.length
      && checkoutTotal >= 0
      && canCommit
      && activeShift
      && quote
      && commerceCalculation
      && finalPayable
      && promotionClaimReady
      && hasCheckoutCapability
      && (!saleContextRequiresOnline || online),
  );

  useEffect(() => {
    if (!online || !identity || !terminal?.deviceSecret || !session?.sessionToken || !sameScope) return;
    void syncPendingCashSales(identity, terminal.deviceSecret, session.sessionToken);
    void syncPendingNonCashSales(identity, terminal.deviceSecret, session.sessionToken);
    void syncPendingCreditSales(identity, terminal.deviceSecret, session.sessionToken);
  }, [identity, online, sameScope, session?.sessionToken, terminal?.deviceSecret]);

  function selectPaymentMethod(method: PaymentMethodRuntimeProjection) {
    setSelectedPaymentGroup(method.settlement_mode === "IMMEDIATE_CASH" ? "CASH" : "NONCASH");
    setSelectedPaymentMethodCode(method.payment_method_code);
    setSelectedDestinationId(method.primary_destination?.destination_id ?? "");
    setShowFallbacks(false);
    setCheckoutMessage(null);
  }

  function selectPaymentGroup(group: Exclude<PaymentGroup, "">) {
    setSelectedPaymentGroup(group);
    setShowFallbacks(false);
    setCheckoutMessage(null);

    if (group === "CREDIT") {
      setSelectedPaymentMethodCode("");
      setSelectedDestinationId("");
      return;
    }
    if (group === "CASH") {
      setSelectedPaymentMethodCode(cashMethod?.payment_method_code ?? "");
      setSelectedDestinationId("");
      return;
    }

    const current = readyNonCashMethods.find((method) => method.payment_method_code === selectedPaymentMethodCode) ?? null;
    const initial = current ?? (readyNonCashMethods.length === 1 ? readyNonCashMethods[0] : null);
    setSelectedPaymentMethodCode(initial?.payment_method_code ?? "");
    setSelectedDestinationId(initial?.primary_destination?.destination_id ?? "");
  }

  async function resolveLatestCheckoutPayable(
    calculation: CommerceCalculationRuntimeItem,
  ) {
    if (!identity || !cart || !quote) return null;
    const redemption = await readLocalLoyaltyRedemption(identity, cart.requestKey);
    const base = calculateFinalPayableRounding(quote.postPromotionTotal, calculation);
    return resolveLocalSaleFinalPayable(base, redemption, cart, currency);
  }

  async function openCheckout() {
    if (!ready || !identity || !cart || !quote || !runtime.commerceCalculation) return;

    const latest = await readPublishedCommerceCalculationRuntime(identity) ?? runtime.commerceCalculation;
    const changed = latest.settings_version_id !== runtime.commerceCalculation.settings_version_id;
    if (changed) {
      await saveLocalCart(identity, { ...cart, commerceCalculation: latest });
    }

    let checkoutPayable;
    try {
      checkoutPayable = await resolveLatestCheckoutPayable(latest);
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOYALTY_REWARD_PAYABLE_RESOLUTION_FAILED"));
      return;
    }
    if (!checkoutPayable) return;

    setCheckoutCalculation(latest);
    setSelectedPaymentGroup("");
    setSelectedPaymentMethodCode("");
    setSelectedDestinationId("");
    setShowFallbacks(false);
    setTendered(checkoutPayable.totalAmount.toFixed(2));
    setCommittedSale(null);
    setPendingNonCashIntent(null);
    setPendingCreditIntent(null);
    setCheckoutState("IDLE");
    setCheckoutMessage(
      changed
        ? "การตั้งค่ายอดชำระมีการอัปเดต ระบบแสดงยอดล่าสุดให้ตรวจสอบก่อนบันทึกขาย"
        : checkoutPayable.totalAmount === 0
          ? "ยอดสุทธิ 0 บาท · ไม่ต้องเลือกวิธีชำระเงิน"
          : null,
    );
    setShowCheckout(true);
  }

  async function revalidateCalculationBeforeCommit() {
    if (!identity || !cart || !commerceCalculation) return null;
    const latest = await readPublishedCommerceCalculationRuntime(identity);
    if (!latest || latest.settings_version_id === commerceCalculation.settings_version_id) return commerceCalculation;

    await saveLocalCart(identity, { ...cart, commerceCalculation: latest });
    setCheckoutCalculation(latest);
    setCheckoutState("ERROR");
    setCheckoutMessage("การตั้งค่ายอดชำระเปลี่ยนระหว่างตรวจบิล กรุณาตรวจยอดล่าสุดแล้วกดยืนยันอีกครั้ง");
    return null;
  }

  async function confirmZeroPayableSale() {
    if (!noPaymentRequired) return;
    if (!identity || !terminal || !actor || !shift || !cart || !commerceCalculation || !session?.sessionToken) return;

    const commitCalculation = await revalidateCalculationBeforeCommit();
    if (!commitCalculation) return;
    let commitPayable;
    try {
      commitPayable = await resolveLatestCheckoutPayable(commitCalculation);
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOYALTY_REWARD_PAYABLE_RESOLUTION_FAILED"));
      return;
    }
    if (!commitPayable || commitPayable.totalAmount !== 0) {
      setCheckoutState("ERROR");
      setCheckoutMessage("ยอดชำระเปลี่ยนแล้ว กรุณาตรวจยอดล่าสุดและเลือกวิธีปิดบิลอีกครั้ง");
      return;
    }

    setCheckoutState("COMMITTING");
    setCheckoutMessage(null);

    const promotionClaimEvidence = claimSession.credentialCode
      ? [{ credential_code: claimSession.credentialCode }]
      : [];

    let localSale: LocalCashSaleSnapshot;
    try {
      localSale = await commitLocalCashSale({
        identity,
        actor,
        shift,
        cart,
        catalogRevision: runtime.catalogRevision,
        commerceCalculation: commitCalculation,
        tenderedAmount: 0,
        onlineAtCommit: online,
        promotionClaimEvidence,
        promotionClaimEvaluation: claimSession.evaluation,
      });
      setCommittedSale(localSale);
      setCheckoutState("PENDING");
      setCheckoutMessage("บันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
      if (promotionClaimEvidence.length) clearPromotionClaimSession();
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOCAL_ZERO_PAYABLE_SALE_COMMIT_FAILED"));
      return;
    }

    if (!online) return;

    void syncCashSaleOutbox(
      identity,
      terminal.deviceSecret,
      session.sessionToken,
      localSale.rootRequestKey,
    ).then((confirmed) => {
      if (!confirmed) {
        setCheckoutState("PENDING");
        setCheckoutMessage("บันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
        return;
      }
      setCommittedSale(confirmed);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `บันทึกขายสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "บันทึกขายเรียบร้อยแล้ว",
      );
    }).catch(() => {
      setCheckoutState("PENDING");
      setCheckoutMessage("บันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
    });
  }

  async function confirmCashSale() {
    if (!isCashSelected || !selectedMethod?.ready) return;
    if (!identity || !terminal || !actor || !shift || !cart || !commerceCalculation || !session?.sessionToken) return;

    const commitCalculation = await revalidateCalculationBeforeCommit();
    if (!commitCalculation) return;
    let commitPayable;
    try {
      commitPayable = await resolveLatestCheckoutPayable(commitCalculation);
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOYALTY_REWARD_PAYABLE_RESOLUTION_FAILED"));
      return;
    }
    if (!commitPayable || !Number.isFinite(tenderedAmount) || tenderedAmount < commitPayable.totalAmount) {
      setCheckoutState("ERROR");
      setCheckoutMessage("จำนวนเงินสดที่รับมาต้องไม่น้อยกว่ายอดชำระ");
      return;
    }

    setCheckoutState("COMMITTING");
    setCheckoutMessage(null);

    const promotionClaimEvidence = claimSession.credentialCode
      ? [{ credential_code: claimSession.credentialCode }]
      : [];

    let localSale: LocalCashSaleSnapshot;
    try {
      localSale = await commitLocalCashSale({
        identity,
        actor,
        shift,
        cart,
        catalogRevision: runtime.catalogRevision,
        commerceCalculation: commitCalculation,
        tenderedAmount,
        onlineAtCommit: online,
        promotionClaimEvidence,
        promotionClaimEvaluation: claimSession.evaluation,
      });
      setCommittedSale(localSale);
      setCheckoutState("PENDING");
      setCheckoutMessage("รับเงินสดและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
      if (promotionClaimEvidence.length) clearPromotionClaimSession();
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOCAL_CASH_SALE_COMMIT_FAILED"));
      return;
    }

    if (!online) return;

    void syncCashSaleOutbox(
      identity,
      terminal.deviceSecret,
      session.sessionToken,
      localSale.rootRequestKey,
    ).then((confirmed) => {
      if (!confirmed) {
        setCheckoutState("PENDING");
        setCheckoutMessage("รับเงินสดและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
        return;
      }
      setCommittedSale(confirmed);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `ขายสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "รับเงินสดและบันทึกขายเรียบร้อยแล้ว",
      );
    }).catch(() => {
      setCheckoutState("PENDING");
      setCheckoutMessage("รับเงินสดและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
    });
  }

  async function confirmNonCashSale() {
    if (!isNonCashSelected || !selectedMethod?.ready || !canCommitNonCashLocally(selectedMethod) || !selectedDestination) return;
    if (!identity || !terminal || !actor || !shift || !cart || !commerceCalculation || !session?.sessionToken) return;

    const commitCalculation = await revalidateCalculationBeforeCommit();
    if (!commitCalculation) return;
    const promotionClaimEvidence = claimSession.credentialCode
      ? [{ credential_code: claimSession.credentialCode }]
      : [];

    let intent: LocalNonCashSaleSnapshot;
    try {
      setCheckoutState("COMMITTING");
      setCheckoutMessage(null);
      intent = await commitLocalNonCashSale({
        identity,
        actor,
        shift,
        cart,
        catalogRevision: runtime.catalogRevision,
        commerceCalculation: commitCalculation,
        paymentMethodCode: selectedMethod.payment_method_code,
        paymentMethodName: selectedMethod.payment_method_name,
        paymentRuntimeCapability: selectedMethod.runtime_capability,
        receivingDestinationId: selectedDestination.destination_id,
        receivingDestinationName: selectedDestination.display_name,
        receivingRouteRole: selectedDestinationRole,
        onlineAtCommit: online,
        promotionClaimEvidence,
      });
      setCommittedSale(intent);
      setPendingNonCashIntent(intent);
      setCheckoutState("PENDING");
      setCheckoutMessage("รับชำระและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
      if (promotionClaimEvidence.length) clearPromotionClaimSession();
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOCAL_NONCASH_SALE_COMMIT_FAILED"));
      return;
    }

    if (!online) return;

    void syncNonCashSaleOutbox(
      identity,
      terminal.deviceSecret,
      session.sessionToken,
      intent.rootRequestKey,
    ).then((confirmed) => {
      setCommittedSale((current) => (
        isNonCashSale(current) && current.rootRequestKey === confirmed.rootRequestKey ? confirmed : current
      ));
      setPendingNonCashIntent(null);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `ขายสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "รับชำระและบันทึกเรียบร้อยแล้ว",
      );
    }).catch(() => {
      setCheckoutState("PENDING");
      setCheckoutMessage("รับชำระและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
    });
  }

  async function confirmCreditSale() {
    if (!isCreditSelected || !localCreditEligible) return;
    if (!identity || !terminal || !actor || !shift || !cart || !commerceCalculation || !session?.sessionToken || !cart.selectedMember) return;

    const commitCalculation = await revalidateCalculationBeforeCommit();
    if (!commitCalculation) return;
    const promotionClaimEvidence = claimSession.credentialCode
      ? [{ credential_code: claimSession.credentialCode }]
      : [];

    let intent: LocalCreditSaleSnapshot;
    try {
      setCheckoutState("COMMITTING");
      setCheckoutMessage(null);
      intent = await commitLocalCreditSaleIntent({
        identity,
        actor,
        shift,
        cart,
        catalogRevision: runtime.catalogRevision,
        commerceCalculation: commitCalculation,
        onlineAtCommit: online,
        availableCredit: effectiveAvailableCredit,
        promotionClaimEvidence,
      });
      setCommittedSale(intent);
      setPendingCreditIntent(intent);
      setCheckoutState("PENDING");
      setCheckoutMessage("บันทึกค้างชำระเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
      if (promotionClaimEvidence.length) clearPromotionClaimSession();
    } catch (error) {
      setCheckoutState("ERROR");
      setCheckoutMessage(friendlyError(error instanceof Error ? error.message : "LOCAL_CREDIT_SALE_COMMIT_FAILED"));
      return;
    }

    if (!online) return;

    void syncCreditSaleOutbox(
      identity,
      terminal.deviceSecret,
      session.sessionToken,
      intent.rootRequestKey,
    ).then((confirmed) => {
      setCommittedSale((current) => (
        isCreditSale(current) && current.rootRequestKey === confirmed.rootRequestKey ? confirmed : current
      ));
      setPendingCreditIntent(null);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `ขายค้างชำระสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "รายการค้างชำระได้รับการยืนยันแล้ว",
      );
    }).catch(() => {
      setCheckoutState("PENDING");
      setCheckoutMessage("บันทึกค้างชำระเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ");
    });
  }

  async function retryPendingNonCash() {
    if (!pendingNonCashIntent || !identity || !terminal?.deviceSecret || !session?.sessionToken || !online) return;
    try {
      setCheckoutState("SYNCING");
      setCheckoutMessage("กำลังตรวจสอบรายการเดิม…");
      const confirmed = await syncNonCashSaleOutbox(
        identity,
        terminal.deviceSecret,
        session.sessionToken,
        pendingNonCashIntent.rootRequestKey,
      );
      setCommittedSale(confirmed);
      setPendingNonCashIntent(null);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `ขายสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "รับชำระสำเร็จและบันทึกเรียบร้อยแล้ว",
      );
      clearPromotionClaimSession();
    } catch (error) {
      const message = error instanceof Error ? error.message : "COMMERCE_POS_SALE_V3_SYNC_FAILED";
      setCheckoutState("PENDING");
      setCheckoutMessage(friendlyError(message));
    }
  }

  async function retryPendingCredit() {
    if (!pendingCreditIntent || !identity || !terminal?.deviceSecret || !session?.sessionToken || !online) return;
    try {
      setCheckoutState("SYNCING");
      setCheckoutMessage("กำลังตรวจสอบรายการค้างชำระเดิม…");
      const confirmed = await syncCreditSaleOutbox(
        identity,
        terminal.deviceSecret,
        session.sessionToken,
        pendingCreditIntent.rootRequestKey,
      );
      setCommittedSale(confirmed);
      setPendingCreditIntent(null);
      setCheckoutState("CONFIRMED");
      setCheckoutMessage(
        confirmed.canonical
          ? `ขายค้างชำระสำเร็จ · ${confirmed.canonical.order_no} · ${formatMoney(confirmed.canonical.grand_total, confirmed.canonical.currency_code)}`
          : "ขายค้างชำระและสร้างลูกหนี้เรียบร้อยแล้ว",
      );
      clearPromotionClaimSession();
    } catch (error) {
      const message = error instanceof Error ? error.message : "COMMERCE_POS_CREDIT_SALE_SYNC_FAILED";
      setCheckoutState("PENDING");
      setCheckoutMessage(friendlyError(message));
    }
  }

  function beginNextSale() {
    clearPromotionClaimSession();
    setShowCheckout(false);
    setCheckoutCalculation(null);
    setCheckoutState("IDLE");
    setCheckoutMessage(null);
    setCommittedSale(null);
    setPendingNonCashIntent(null);
    setPendingCreditIntent(null);
    setSelectedPaymentGroup("");
    setSelectedPaymentMethodCode("");
    setSelectedDestinationId("");
    setTendered("");
  }

  if (!mountNode || !terminal || !identity || !actor || !sameScope) return null;

  const unavailableReason = !cartLines.length
    ? "เพิ่มสินค้าลงบิลก่อนชำระเงิน"
    : !quote
      ? "กำลังเตรียมยอดล่าสุด"
      : !commerceCalculation || !finalPayable
        ? "กำลังเตรียมกฎคำนวณยอด"
      : hasBearerPromotionClaim && !online
        ? "คูปองแบบใช้รหัสต้องเชื่อมต่ออินเทอร์เน็ตก่อนชำระเงิน"
      : hasBearerPromotionClaim && !claimSession.evaluation
        ? "กรุณาตรวจรหัสคูปองก่อนชำระเงิน"
      : hasBearerPromotionClaim && claimSession.evaluation?.validation_state === "INVALID"
        ? "รหัสคูปองใช้ไม่ได้ · กรุณาล้างหรือกรอกรหัสใหม่"
      : hasBearerPromotionClaim && quote.quoteSource !== "SERVER_COMMERCE_QUOTE_V1"
        ? "กำลังตรวจสอบยอดที่มีคูปอง"
      : !online && hasPromotionContext && promotionRequiresOnline
        ? "บิลนี้มีโปรโมชั่นที่ต้องตรวจสอบผ่านอินเทอร์เน็ตก่อนชำระ"
      : !activeShift
        ? "ต้องเปิดกะก่อนบันทึกขาย"
      : !canCommit
        ? "พนักงานไม่มีสิทธิ์ยืนยันการขาย"
      : noPaymentRequired
        ? "ยอดสุทธิ 0 บาท · พร้อมบันทึกขาย"
      : !hasCheckoutCapability
        ? "ยังไม่มีวิธีปิดบิลที่พร้อมใช้สำหรับลูกค้ารายนี้"
        : paymentRuntime.runtimeError && paymentRuntime.runtime.initialized
          ? "ใช้ข้อมูลวิธีรับเงินล่าสุดที่มีอยู่ · ระบบจะอัปเดตให้อัตโนมัติ"
          : "พร้อมบันทึกขาย";

  const nonCashNotReady = nonCashMethods.find((method) => !method.ready || !canCommitNonCashLocally(method)) ?? null;
  const nonCashNeedsMethodChoice = selectedPaymentGroup === "NONCASH" && readyNonCashMethods.length > 1 && !selectedMethod;
  const confirmedNonCash = isNonCashSale(committedSale);
  const confirmedCredit = isCreditSale(committedSale);
  const confirmedCash = isCashSale(committedSale);
  const projectedAvailableAfter = effectiveAvailableCredit == null
    ? null
    : Math.max(0, effectiveAvailableCredit - checkoutTotal);

  return (
    <>
      {createPortal(
        <div className="cash-sale-runtime">
          <button
            className="cash-sale-runtime__button"
            type="button"
            disabled={!ready || checkoutState === "COMMITTING" || checkoutState === "SYNCING"}
            onClick={() => void openCheckout()}
          >
            {noPaymentRequired ? "ยืนยันรายการ" : "ชำระเงิน"}
          </button>
          <p className="cash-sale-runtime__note">{unavailableReason}</p>
        </div>,
        mountNode,
      )}

      {showCheckout ? createPortal(
        <div className="cash-sale-modal-backdrop" role="presentation">
          <section className="cash-sale-modal" role="dialog" aria-modal="true" aria-labelledby="cash-sale-title">
            <div className="cash-sale-modal__header">
              <div>
                <p>{noPaymentRequired ? "ไม่ต้องรับชำระ" : "รับชำระ / ชำระภายหลัง"}</p>
                <h2 id="cash-sale-title">ตรวจสอบก่อนบันทึกขาย</h2>
              </div>
              {!committedSale && checkoutState !== "PENDING" ? (
                <button className="cash-sale-modal__close" type="button" onClick={() => setShowCheckout(false)} aria-label="ปิด">×</button>
              ) : null}
            </div>

            <div className="cash-sale-modal__total">
              <p>ยอดชำระ</p>
              <strong>{formatMoney(displayTotal, displayCurrency)}</strong>
            </div>

            {!committedSale && finalPayable && Math.abs(finalPayable.roundingAmount) >= 0.005 ? (
              <div className="cash-sale-modal__row">
                <div><p>ยอดก่อนปัดเศษ</p><strong>{formatMoney(finalPayable.preRoundingTotal, currency)}</strong></div>
                <div><p>ปรับเศษ</p><strong>{formatMoney(finalPayable.roundingAmount, currency)}</strong></div>
              </div>
            ) : null}

            {!committedSale && noPaymentRequired ? (
              <div className="cash-sale-modal__status">ยอดสุทธิ 0 บาท · ไม่ต้องเลือกวิธีชำระเงิน</div>
            ) : null}

            {!committedSale && !noPaymentRequired ? (
              <div className="cash-sale-modal__methods">
                <p className="cash-sale-modal__section-title">วิธีปิดบิล</p>
                <div className="cash-sale-modal__method-grid">
                  {cashMethod ? (
                    <button
                      type="button"
                      onClick={() => selectPaymentGroup("CASH")}
                      disabled={!cashMethod.ready || checkoutState === "PENDING"}
                      className={`cash-sale-modal__method ${selectedPaymentGroup === "CASH" ? "cash-sale-modal__method--selected" : ""} ${cashMethod.ready ? "" : "cash-sale-modal__method--not-ready"}`}
                    >
                      <strong>เงินสด</strong>
                      <span>รับเงินและทอนที่หน้าร้าน</span>
                    </button>
                  ) : null}
                  {nonCashMethods.length ? (
                    <button
                      type="button"
                      onClick={() => selectPaymentGroup("NONCASH")}
                      disabled={!readyNonCashMethods.length || checkoutState === "PENDING"}
                      className={`cash-sale-modal__method ${selectedPaymentGroup === "NONCASH" ? "cash-sale-modal__method--selected" : ""} ${readyNonCashMethods.length ? "" : "cash-sale-modal__method--not-ready"}`}
                    >
                      <strong>สแกน / โอน</strong>
                      <span>ใช้ช่องทางหลักอัตโนมัติ</span>
                    </button>
                  ) : null}
                  {cart?.selectedMember && customerCredit ? (
                    <button
                      type="button"
                      onClick={() => selectPaymentGroup("CREDIT")}
                      disabled={!localCreditEligible || checkoutState === "PENDING"}
                      className={`cash-sale-modal__method ${selectedPaymentGroup === "CREDIT" ? "cash-sale-modal__method--selected" : ""} ${localCreditEligible ? "" : "cash-sale-modal__method--not-ready"}`}
                      title={localCreditReason ?? undefined}
                    >
                      <strong>ชำระภายหลัง</strong>
                      <span>{creditAvailabilityCopy ?? localCreditReason}</span>
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!committedSale && selectedPaymentGroup === "NONCASH" && !readyNonCashMethods.length && nonCashNotReady ? (
              <div className="cash-sale-modal__status cash-sale-modal__status--pending">
                {paymentRuntimeReason(nonCashNotReady) ?? "วิธีสแกน/โอนนี้ต้องยืนยันกับระบบภายนอกก่อน จึงยังปิดบิลไม่ได้"}
              </div>
            ) : null}

            {!committedSale && nonCashNeedsMethodChoice ? (
              <div className="cash-sale-modal__fallback">
                <p className="cash-sale-modal__section-title">เลือกวิธีสแกน / โอน</p>
                <div className="cash-sale-modal__fallback-list">
                  {readyNonCashMethods.map((method) => (
                    <button key={method.payment_method_code} type="button" onClick={() => selectPaymentMethod(method)}>
                      {method.payment_method_name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {!committedSale && selectedMethod && !selectedMethod.ready ? (
              <div className="cash-sale-modal__status cash-sale-modal__status--pending">
                {paymentRuntimeReason(selectedMethod)}
              </div>
            ) : null}

            {!committedSale && isNonCashSelected && selectedMethod?.ready && selectedDestination ? (
              <>
                <DestinationCard destination={selectedDestination} role={selectedDestinationRole} />
                {selectedMethod.fallback_destinations.length ? (
                  <div className="cash-sale-modal__fallback">
                    <button type="button" disabled={checkoutState === "PENDING"} onClick={() => setShowFallbacks((value) => !value)}>
                      {showFallbacks ? "ปิดรายการสำรอง" : "เปลี่ยนช่องทางรับเงิน"}
                    </button>
                    {showFallbacks ? (
                      <div className="cash-sale-modal__fallback-list">
                        <button
                          type="button"
                          className={selectedDestinationId === selectedMethod.primary_destination?.destination_id ? "is-selected" : ""}
                          onClick={() => setSelectedDestinationId(selectedMethod.primary_destination?.destination_id ?? "")}
                        >
                          หลัก · {selectedMethod.primary_destination?.display_name}
                        </button>
                        {selectedMethod.fallback_destinations.map((destination) => (
                          <button
                            key={destination.destination_id}
                            type="button"
                            className={selectedDestinationId === destination.destination_id ? "is-selected" : ""}
                            onClick={() => setSelectedDestinationId(destination.destination_id)}
                          >
                            สำรอง · {destination.display_name}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}

            {!committedSale && isCashSelected && selectedMethod?.ready ? (
              <>
                <label className="cash-sale-modal__field">
                  <span>รับเงินสด</span>
                  <input
                    inputMode="decimal"
                    type="number"
                    min={checkoutTotal}
                    step="0.01"
                    value={tendered}
                    onChange={(event) => setTendered(event.target.value)}
                    autoFocus
                  />
                </label>
                <div className="cash-sale-modal__row">
                  <button className="cash-sale-modal__exact" type="button" onClick={() => setTendered(checkoutTotal.toFixed(2))}>รับพอดี</button>
                  <div><p>เงินทอน</p><strong>{formatMoney(change, currency)}</strong></div>
                </div>
              </>
            ) : null}

            {!committedSale && isCreditSelected && cart?.selectedMember && customerCredit ? (
              <div className="cash-sale-modal__destination">
                <div className="cash-sale-modal__destination-head">
                  <div><p>ลูกค้าที่จะบันทึกค้างชำระ</p><strong>{cart.selectedMember.customerName}</strong></div>
                  <span>ค้างชำระ</span>
                </div>
                <p>ยอดค้างล่าสุด: <strong>{formatMoney(customerCredit.current_outstanding, customerCredit.currency_code)}</strong></p>
                <p>วงเงินพร้อมใช้ตามระบบ: <strong>{formatMoney(canonicalAvailableCredit ?? 0, customerCredit.currency_code)}</strong></p>
                {runtime.pendingLocalCreditExposure > 0 ? (
                  <>
                    <p>รายการชำระภายหลังที่รอส่งข้อมูล: <strong>{formatMoney(runtime.pendingLocalCreditExposure, customerCredit.currency_code)}</strong></p>
                    <p>วงเงินที่ใช้กับบิลใหม่ได้ตอนนี้: <strong>{formatMoney(effectiveAvailableCredit ?? 0, customerCredit.currency_code)}</strong></p>
                  </>
                ) : null}
                <p>ยอดบิลนี้: <strong>{formatMoney(checkoutTotal, currency)}</strong></p>
                {projectedAvailableAfter != null ? <p>วงเงินพร้อมใช้หลังบิลนี้โดยประมาณ: <strong>{formatMoney(projectedAvailableAfter, customerCredit.currency_code)}</strong></p> : null}
                <p>กำหนดชำระ: <strong>{customerCredit.payment_term_days} วัน</strong></p>
                <p>ระบบจะบันทึกรายการและตรวจสอบวงเงินส่วนที่เหลือให้อัตโนมัติ</p>
              </div>
            ) : null}

            {confirmedCash ? (
              committedSale.localTotal === 0 ? (
                <div className="cash-sale-modal__destination">
                  <p>สถานะ</p>
                  <strong>บันทึกขายเรียบร้อยแล้ว</strong>
                  <p>ยอดสุทธิ 0 บาท · ไม่มีการรับชำระ</p>
                </div>
              ) : (
                <div className="cash-sale-modal__row">
                  <div><p>รับเงิน</p><strong>{formatMoney(committedSale.tenderedAmount, committedSale.currencyCode)}</strong></div>
                  <div><p>เงินทอน</p><strong>{formatMoney(committedSale.changeAmount, committedSale.currencyCode)}</strong></div>
                </div>
              )
            ) : null}

            {confirmedNonCash ? (
              <div className="cash-sale-modal__destination">
                <p>รับชำระผ่าน</p>
                <strong>{committedSale.receivingDestinationName}</strong>
                <p>{committedSale.canonical ? "รับชำระและบันทึกขายเรียบร้อยแล้ว" : "รับชำระและบันทึกขายเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ"}</p>
              </div>
            ) : null}

            {confirmedCredit ? (
              <div className="cash-sale-modal__destination">
                <p>สถานะ</p>
                <strong>{committedSale.canonical ? "บันทึกขายค้างชำระและสร้างลูกหนี้แล้ว" : "บันทึกค้างชำระเรียบร้อยแล้ว · ระบบจะดำเนินการต่ออัตโนมัติ"}</strong>
                <p>{committedSale.canonical ? "รายการนี้ยังไม่มีการรับเงินจริงจนกว่าจะมีการชำระในภายหลัง" : "ระบบจะตรวจสอบและบันทึกข้อมูลส่วนที่เหลือให้อัตโนมัติ"}</p>
              </div>
            ) : null}

            {checkoutMessage ? (
              <div className={`cash-sale-modal__status ${checkoutState === "PENDING" ? "cash-sale-modal__status--pending" : checkoutState === "ERROR" ? "cash-sale-modal__status--error" : ""}`}>
                {checkoutMessage}
              </div>
            ) : null}

            <div className="cash-sale-modal__actions">
              {committedSale ? (
                <button className="cash-sale-modal__primary" type="button" onClick={beginNextSale}>เริ่มบิลใหม่</button>
              ) : checkoutState === "PENDING" && pendingCreditIntent ? (
                <button className="cash-sale-modal__primary" type="button" disabled={!online} onClick={() => void retryPendingCredit()}>
                  ตรวจสอบรายการค้างชำระเดิมอีกครั้ง
                </button>
              ) : checkoutState === "PENDING" && pendingNonCashIntent ? (
                <button className="cash-sale-modal__primary" type="button" disabled={!online} onClick={() => void retryPendingNonCash()}>
                  ตรวจสอบรายการเดิมอีกครั้ง
                </button>
              ) : (
                <>
                  <button className="cash-sale-modal__secondary" type="button" disabled={checkoutState === "COMMITTING" || checkoutState === "SYNCING"} onClick={() => setShowCheckout(false)}>ยกเลิก</button>
                  {noPaymentRequired ? (
                    <button
                      className="cash-sale-modal__primary"
                      type="button"
                      disabled={checkoutState === "COMMITTING" || checkoutState === "SYNCING"}
                      onClick={() => void confirmZeroPayableSale()}
                    >
                      {checkoutState === "COMMITTING" ? "กำลังบันทึก…" : `ยืนยันรายการ · ${formatMoney(checkoutTotal, currency)}`}
                    </button>
                  ) : isCashSelected && selectedMethod?.ready ? (
                    <button
                      className="cash-sale-modal__primary"
                      type="button"
                      disabled={!Number.isFinite(tenderedAmount) || tenderedAmount < checkoutTotal || checkoutState === "COMMITTING"}
                      onClick={() => void confirmCashSale()}
                    >
                      {checkoutState === "COMMITTING" ? "กำลังบันทึก…" : `รับเงินสด · บันทึก ${formatMoney(checkoutTotal, currency)}`}
                    </button>
                  ) : isNonCashSelected && selectedMethod?.ready && selectedDestination ? (
                    <button
                      className="cash-sale-modal__primary"
                      type="button"
                      disabled={!canCommitNonCashLocally(selectedMethod) || checkoutState === "COMMITTING" || checkoutState === "SYNCING"}
                      onClick={() => void confirmNonCashSale()}
                    >
                      {checkoutState === "COMMITTING" ? "กำลังบันทึก…" : checkoutState === "SYNCING" ? "กำลังตรวจสอบ…" : `ได้รับเงินแล้ว · บันทึก ${formatMoney(checkoutTotal, currency)}`}
                    </button>
                  ) : isCreditSelected && customerCredit ? (
                    <button
                      className="cash-sale-modal__primary"
                      type="button"
                      disabled={!localCreditEligible || checkoutState === "COMMITTING" || checkoutState === "SYNCING"}
                      onClick={() => void confirmCreditSale()}
                    >
                      {checkoutState === "COMMITTING" ? "กำลังบันทึก…" : `บันทึกค้างชำระ · ${formatMoney(checkoutTotal, currency)}`}
                    </button>
                  ) : (
                    <button className="cash-sale-modal__primary" type="button" disabled>
                      {selectedPaymentGroup ? "วิธีปิดบิลนี้ยังไม่พร้อม" : "เลือกวิธีปิดบิล"}
                    </button>
                  )}
                </>
              )}
            </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
